/**
 * @file A WordPress-like hook system for JavaScript.
 *
 * This file demonstrates a simple hook system for JavaScript based on the hook
 * system in WordPress. The purpose of this is to make your code extensible and
 * allowing other developers to hook into your code with their own callbacks.
 *
 * There are other ways to do this, but this will feel right at home for
 * WordPress developers.
 *
 * @author Rheinard Korf
 * @license GPL2 (https://www.gnu.org/licenses/gpl-2.0.html)
 *
 * @requires underscore.js (http://underscorejs.org/)
 */

/**
 * Hooks object
 *
 * This object needs to be declared early so that it can be used in code.
 * Preferably at a global scope.
 */
var DpitHooks = DpitHooks || {}; // Extend Hooks if exists or create new Hooks object.

DpitHooks.actions = DpitHooks.actions || {}; // Registered actions
DpitHooks.filters = DpitHooks.filters || {}; // Registered filters

/**
  * Add a new Action callback to Hooks.actions
  *
  * @param tag The tag specified by do_action()
  * @param callback The callback function to call when do_action() is called
  * @param priority The order in which to call the callbacks. Default: 10 (like WordPress)
  */
DpitHooks.add_action = function (tag, callback, priority) {
	if (typeof priority === 'undefined') {
		priority = 10;
	}

	// If the tag doesn't exist, create it.
	DpitHooks.actions[tag] = DpitHooks.actions[tag] || [];
	DpitHooks.actions[tag].push({ priority, callback });
};

/**
  * Add a new Filter callback to Hooks.filters
  *
  * @param tag The tag specified by apply_filters()
  * @param callback The callback function to call when apply_filters() is called
  * @param priority Priority of filter to apply. Default: 10 (like WordPress)
  */
DpitHooks.add_filter = function (tag, callback, priority) {
	if (typeof priority === 'undefined') {
		priority = 10;
	}

	// If the tag doesn't exist, create it.
	DpitHooks.filters[tag] = DpitHooks.filters[tag] || [];
	DpitHooks.filters[tag].push({ priority, callback });
};

/**
  * Remove an Anction callback from Hooks.actions
  *
  * Must be the exact same callback signature.
  * Warning: Anonymous functions can not be removed.
  * @param tag The tag specified by do_action()
  * @param callback The callback function to remove
  */
DpitHooks.remove_action = function (tag, callback) {
	DpitHooks.actions[tag] = DpitHooks.actions[tag] || [];

	DpitHooks.actions[tag].forEach((filter, i) => {
		if (filter.callback === callback) {
			DpitHooks.actions[tag].splice(i, 1);
		}
	});
};

/**
  * Remove a Filter callback from Hooks.filters
  *
  * Must be the exact same callback signature.
  * Warning: Anonymous functions can not be removed.
  * @param tag The tag specified by apply_filters()
  * @param callback The callback function to remove
  */
DpitHooks.remove_filter = function (tag, callback) {
	DpitHooks.filters[tag] = DpitHooks.filters[tag] || [];

	DpitHooks.filters[tag].forEach((filter, i) => {
		if (filter.callback === callback) {
			DpitHooks.filters[tag].splice(i, 1);
		}
	});
};

/**
  * Calls actions that are stored in Hooks.actions for a specific tag or nothing
  * if there are no actions to call.
  *
  * @param tag A registered tag in Hook.actions
  * @options Optional JavaScript object to pass to the callbacks
  */
DpitHooks.do_action = function (tag, options) {
	const actions = [];

	if (typeof DpitHooks.actions[tag] !== 'undefined' && DpitHooks.actions[tag].length > 0) {
		DpitHooks.actions[tag].forEach((hook) => {
			actions[hook.priority] = actions[hook.priority] || [];
			actions[hook.priority].push(hook.callback);
		});

		actions.forEach((hooks) => {
			hooks.forEach((callback) => {
				callback(options);
			});
		});
	}
};

/**
  * Calls filters that are stored in Hooks.filters for a specific tag or return
  * original value if no filters exist.
  *
  * @param tag A registered tag in Hook.filters
  * @options Optional JavaScript object to pass to the callbacks
  */
DpitHooks.apply_filters = function (tag, value, options) {
	const filters = [];
	let res = value;
	if (typeof DpitHooks.filters[tag] !== 'undefined' && DpitHooks.filters[tag].length > 0) {
		DpitHooks.filters[tag].forEach((hook) => {
			filters[hook.priority] = filters[hook.priority] || [];
			filters[hook.priority].push(hook.callback);
		});

		filters.forEach((hooks) => {
			hooks.forEach((callback) => {
				res = callback(res, options);
			});
		});
	}

	return res;
};

export default DpitHooks;
